import { useEffect, useMemo, useState } from 'react'
import { Trans } from 'react-i18next'
import clsx from 'clsx'
import fastDeepEqual from 'fast-deep-equal'

import { ITableColumn } from '@makyoapp/makyo-sdk/models/Table'
import { isNumValid, RatingColOptionsOnly } from '@makyoapp/makyo-common'
import {
  Button,
  ColorVariant,
  DateTimeInput,
  getAccentColor,
  MaterialIcon,
  safeAdjustColor,
  Select,
  Slider,
  Switch,
  TextField,
} from '@makyoapp/makyo-tw-ui'

import { IDBTable } from '@/core/db'
import { UserColumnFilterType, UserColumnFilterValue } from '@/shared/builder/types/user-filter'

import { FilterDateRangeField } from './FilterDateRangeField'

// ========================================================================================= //
//                                      Render Field (single)
// ========================================================================================= //

interface RenderFieldProps {
  setting: UserColumnFilterValue
  value: any
  onChange: (value?: any) => void
  column: ITableColumn
  error?: any
  rowOptions: { id: string; label: string; value: any }[]
  bgColor: ColorVariant
}

function RenderField({ setting, value, onChange, rowOptions, column, error, bgColor }: RenderFieldProps) {
  const colSettings = column._Settings as any

  const { filterType, multiple } = setting

  switch (filterType) {
    // ✅ ok
    case UserColumnFilterType.NumberSelection:
    case UserColumnFilterType.TextSelection:
    case UserColumnFilterType.TextRadio:
    case UserColumnFilterType.TextSegmented: {
      const currValue = multiple
        ? Array.isArray(value)
          ? value
          : []
        : typeof value === 'undefined' || value === null
          ? ''
          : value.toString()

      return (
        <Select
          type={
            {
              'text-selection': 'chip',
              'text-radio': 'radio',
              'text-segmented': 'segmented',
              'num-selection': 'chip',
            }[filterType] as any
          }
          multiple={multiple}
          value={currValue}
          onChange={(next) => {
            if (multiple) {
              onChange(Array.isArray(next) ? next : [next])
            } else {
              if (!value || value !== next) {
                onChange(next)
              } else {
                onChange(undefined)
              }
            }
          }}
          options={rowOptions}
        />
      )
    }

    // ✅ ok
    case UserColumnFilterType.NumberExactAmount: {
      return (
        <div className='flex gap-4'>
          <TextField
            type='number'
            placeholder='Enter a number'
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => {
              const val = e.currentTarget.value
              const numVal = Number(val)
              const isNum = !isNaN(numVal) || val === '-'
              onChange(isNum ? val : '0')
            }}
            onClear={() => {
              onChange(undefined)
            }}
          />
        </div>
      )
    }

    // ✅ ok
    case UserColumnFilterType.NumberRange: {
      const currValue: Array<string | null> = Array.isArray(value) ? value : [null, null]
      const valStart = currValue[0] ?? null
      const valEnd = currValue[1] ?? null

      return (
        <div>
          <div className='flex gap-4'>
            <TextField
              value={valStart ?? ''}
              placeholder='Min'
              onChange={(e) => {
                const start = e.currentTarget.value || null
                onChange([start, valEnd])
              }}
              onClear={() => {
                onChange([null, valEnd])
              }}
            />
            <TextField
              value={valEnd ?? ''}
              placeholder='Max'
              onChange={(e) => {
                const end = e.currentTarget.value || null
                onChange([valStart, end])
              }}
              onClear={() => {
                onChange([valStart, null])
              }}
              error={error?.max}
            />
          </div>
        </div>
      )
    }

    // ✅ ok
    case UserColumnFilterType.DateExactDate: {
      const dt = typeof value === 'string' ? new Date(value).toISOString() : null
      return (
        <div>
          <DateTimeInput
            value={dt ? dt : null}
            onChange={(next) => {
              onChange(next)
            }}
          />
        </div>
      )
    }

    // ✅ ok
    case UserColumnFilterType.DateDateRange: {
      const start = Array.isArray(value) && typeof value[0] === 'string' ? new Date(value[0]) : undefined
      const end = Array.isArray(value) && typeof value[1] === 'string' ? new Date(value[1]) : undefined

      return (
        <div>
          <FilterDateRangeField
            start={start}
            end={end}
            onChange={(s, e) => {
              onChange([s, e])
            }}
          />
        </div>
      )
    }

    // ✅ ok
    case UserColumnFilterType.ContentNotEmpty:
    case UserColumnFilterType.BooleanToggle: {
      return <Switch checked={Boolean(value)} onChange={onChange} />
    }

    // ✅ ok
    case UserColumnFilterType.RatingAmountWithin: {
      const { ratingMax = 5, ratingMin = 1 } = colSettings as RatingColOptionsOnly
      return (
        <div className='pr-4'>
          <Slider
            value={value && isNumValid(Number(value)) ? Number(value) : ratingMin}
            onChange={(val) => {
              onChange(val)
            }}
            minValue={ratingMin}
            maxValue={ratingMax}
            styles={
              {
                // '--slider-track-label-color': `var(--field-color-label)`,
                '--slider-track-fill-color': `var(--color-on-${bgColor})`,
                '--slider-track-thumb-color': `var(--color-${safeAdjustColor(bgColor, 20)})`,
                '--slider-tooltip-bg-color': `var(--color-on-${bgColor})`,
                '--slider-tooltip-text-color': `var(--color-${bgColor})`,
              } as any
            }
          />
        </div>
      )
    }

    // ✅ ok
    case UserColumnFilterType.RatingAtLeast: {
      const { ratingMax = 5 } = colSettings as RatingColOptionsOnly

      // generate options for rating range (1 - 5.0 / 1 - 10.0)
      const options = [
        { id: '1.0', label: '1.0', value: 1 },
        { id: '2.0', label: '2.0', value: 2 },
        { id: '3.0', label: '3.0', value: 3 },
        { id: '4.0', label: '4.0', value: 4 },
        { id: '4.5', label: '5', value: 5 },
      ]

      return (
        <Select
          type='segmented'
          value={typeof value === 'undefined' || value === null ? '' : value.value}
          onChange={(next) => {
            if (!value || value !== next) {
              let nextVal = {
                type: 'minimum',
                value: parseFloat(next as string),
              }
              onChange(next === '' ? undefined : nextVal)
            } else {
              onChange(undefined)
            }
          }}
          options={options.map((option) => {
            return {
              id: option.id,
              label: option.label,
              value: String(option.value),
            }
          })}
          renderOption={(opt) => {
            return (
              <div className='flex items-center gap-[2px]'>
                {[...Array(Number(opt.value))].map((_, i) => (
                  <MaterialIcon icon='star' key={i} size={14} fill />
                ))}
              </div>
            )
          }}
        />
      )
    }

    default:
      return null
  }
}

// ========================================================================================= //
//                                      Filter Fields
// ========================================================================================= //

export interface FilterFieldsProps {
  value?: any
  onSubmit: (values: any) => void
  activeIndex: number
  settings: UserColumnFilterValue[]
  table: IDBTable
  bgColor: ColorVariant
  getTypeDescription: (type: UserColumnFilterType) => string | null
  getRowOptions: (columnID: string) => {
    id: string
    label: string
    value: any
  }[]
}

/**
 * FilterField for filter (fullScreen) view
 */
export function FilterFields({
  value,
  onSubmit,
  activeIndex,
  settings,
  table,
  bgColor,
  getTypeDescription,
  getRowOptions,
}: FilterFieldsProps) {
  // Local State
  const [tempValues, setTempValues] = useState<Record<string, any>>(value)
  const [errors, setErrors] = useState<Record<string, any>>({})

  useEffect(() => {
    if (!fastDeepEqual(tempValues, value)) {
      setTempValues(value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const handleSubmit = () => {
    let values = tempValues
    let isValid = true

    const isRangeField = settings.some((setting) => setting.filterType === UserColumnFilterType.NumberRange)
    if (isRangeField) {
      const rangeFields = settings.filter((setting) => setting.filterType === UserColumnFilterType.NumberRange)
      rangeFields.forEach((field) => {
        if (!values[field.column]) {
          return
        }
        // if min field is not set, set it to 0
        if (values[field.column][0] === null) {
          values[field.column][0] = 0
        }
        // if min is specified, max field is required
        if (values[field.column][0] !== null && values[field.column][1] === null) {
          setErrors((prev) => ({ ...prev, [field.column]: { max: 'Max value is required' } }))
          isValid = false
        }
      })
    }

    if (isValid) {
      onSubmit(values)
      setTempValues({})
    }
  }

  // ================================= colors ================================
  const { accentColor } = useMemo(() => {
    const [palette, tonePart] = bgColor ? bgColor.split('-') : ['primary', '95']
    const bgTone = parseInt(tonePart, 10)

    let accentColor = safeAdjustColor(bgColor, 60)
    if (bgTone <= 30) {
      accentColor = `${palette}-95` as ColorVariant
    } else if (bgTone >= 20) {
      accentColor = `${palette}-20` as ColorVariant
    }

    return {
      accentColor,
    }
  }, [bgColor])

  // ================================ render ================================ //

  const isAllFilters = activeIndex === -1

  return (
    <div id='comp-filter-fields' className={clsx(`md:px-4 text-on-${bgColor}`)}>
      {/* Modal Content */}
      <div className='pt-6 pb-8 flex flex-col gap-8'>
        {settings
          .filter((_, index) => index === activeIndex || isAllFilters)
          .map((setting, i) => {
            const column = table.columnsMap.get(setting.column)
            if (!setting.showOnFilter || !column) return null

            // Field Title
            const desc = getTypeDescription(setting.filterType)
            const fieldTitle = isAllFilters ? (
              <div className='pb-4 flex items-start gap-2'>
                <div>
                  <div className='text-sm md:text-base font-semibold'>{setting.label || column?._Name || ''}</div>
                  {desc && <div className='text-[13px] opacity-60'>({desc})</div>}
                </div>
              </div>
            ) : null

            // Single Field
            return (
              <div key={`${setting.column}-${i}`}>
                {fieldTitle}

                <RenderField
                  setting={setting}
                  error={errors[setting.column]}
                  value={tempValues[setting.column]}
                  onChange={(next) => {
                    setTempValues((prev) => {
                      let newValues = { ...prev }
                      if (typeof next === 'undefined') {
                        delete newValues[setting.column]
                      } else if (Array.isArray(next) && next.length === 0) {
                        delete newValues[setting.column]
                      } else {
                        newValues[setting.column] = next
                      }
                      return newValues
                    })

                    if (errors[setting.column]) {
                      setErrors((prev) => {
                        let newErrors = { ...prev }
                        delete newErrors[setting.column]
                        return newErrors
                      })
                    }
                  }}
                  column={column}
                  rowOptions={getRowOptions(setting.column)}
                  bgColor={bgColor}
                />
              </div>
            )
          })}
      </div>

      <hr className={`my-4 border-on-${bgColor}/30`} />

      {/* Footer */}
      <div className='flex items-center justify-between gap-4'>
        <Button
          colorVariant={bgColor}
          border
          onClick={() => {
            setTempValues({})
            setErrors({})
          }}
        >
          <Trans>Clear</Trans>
        </Button>

        <div className='flex items-center gap-2'>
          <Button
            colorVariant={bgColor}
            border
            onClick={handleSubmit}
            disabled={
              Object.keys(errors).length > 0 &&
              Object.values(errors).some((error) => error !== undefined && error !== null)
            }
          >
            <Trans>Apply</Trans>
          </Button>
        </div>
      </div>
    </div>
  )
}
